import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLocation } from '../../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../../contexts/BusinessTypeContext';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../../utils/api';
import { GoatMaster, VaccineType, MasterData } from '../../../types/api';
import { useMasterData } from '../../../hooks/useApi';

interface InventoryItem {
  id: string;
  name: string;
  unit: string;
  category: string;
  is_active?: boolean;
}

type TreatmentMode = 'vaccination' | 'deworming';

interface GoatVaccinationFormData {
  treatment_mode: TreatmentMode;
  health_date: string;
  vaccine_type: VaccineType;
  item_id: string;  // Inventory item ID (for vaccination)
  medicine_name: string;  // Medicine name from master data (for deworming)
  dose: string;
  route: string;
  notes?: string;
}

const vaccinationSchema = yup.object({
  treatment_mode: yup.string().oneOf(['vaccination', 'deworming']).required('Treatment mode is required'),
  health_date: yup.string().required('Health date is required'),
  vaccine_type: yup.string().when('treatment_mode', {
    is: 'vaccination',
    then: (schema) => schema.oneOf(['PPR', 'ET_TT', 'FMD_HS_BQ', 'GOAT_POX']).required('Vaccine type is required'),
    otherwise: (schema) => schema.optional(),
  }),
  item_id: yup.string().when('treatment_mode', {
    is: 'vaccination',
    then: (schema) => schema.required('Medicine is required'),
    otherwise: (schema) => schema.optional(),
  }),
  medicine_name: yup.string().when('treatment_mode', {
    is: 'deworming',
    then: (schema) => schema.required('Medicine is required'),
    otherwise: (schema) => schema.optional(),
  }),
  dose: yup.string().required('Dose is required'),
  route: yup.string().when('treatment_mode', {
    is: 'vaccination',
    then: (schema) => schema.required('Route is required'),
    otherwise: (schema) => schema.optional(),
  }),
  notes: yup.string().optional(),
});

// Vaccine defaults
const VACCINE_DEFAULTS: Record<VaccineType, { medicine: string; dose: string; route: string; minAgeMonths: number }> = {
  PPR: { medicine: 'Raksha PPR Vaccine', dose: '1 ml', route: 'Subcutaneous (SC)', minAgeMonths: 3 },
  ET_TT: { medicine: 'Raksha ET + TT Vaccine', dose: '1 ml', route: 'Subcutaneous (SC)', minAgeMonths: 3 },
  FMD_HS_BQ: { medicine: 'Raksha Triovac (FMD + HS + BQ)', dose: '1 ml', route: 'Intramuscular (IM)', minAgeMonths: 4 },
  GOAT_POX: { medicine: 'Raksha Goat Pox Vaccine', dose: '0.5 ml', route: 'Subcutaneous (SC)', minAgeMonths: 3 },
};

const GoatVaccinationForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [error, setError] = useState('');
  const [goats, setGoats] = useState<GoatMaster[]>([]);
  const [loadingGoats, setLoadingGoats] = useState(true);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [includeMode, setIncludeMode] = useState(true); // true = include mode, false = exclude mode
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  
  // Load medicines from master data (for matching vaccine defaults)
  const { data: medicinesData, loading: loadingMedicines } = useMasterData('goat_medicine_types', selectedLocation?.id || null);
  const medicines = medicinesData || [];

  const form = useForm<GoatVaccinationFormData>({
    resolver: yupResolver(vaccinationSchema) as any,
    defaultValues: {
      treatment_mode: 'vaccination',
      health_date: new Date().toISOString().split('T')[0],
      vaccine_type: 'PPR',
      item_id: '',
      medicine_name: '',
      dose: VACCINE_DEFAULTS.PPR.dose,
      route: VACCINE_DEFAULTS.PPR.route,
      notes: '',
    },
  });

  const treatmentMode = form.watch('treatment_mode');
  const vaccineType = form.watch('vaccine_type');

  // Load inventory items for the selected location
  useEffect(() => {
    const loadInventoryItems = async () => {
      if (!selectedLocation) {
        setInventoryItems([]);
        return;
      }
      setLoadingInventory(true);
      try {
        const response = await apiClient.get('/inventory/items', {
          params: { location_id: selectedLocation.id }
        });
        // Filter for medicine category items only (active items)
        const medicineItems = (response.data || []).filter(
          (item: InventoryItem) => item.category === 'medicine' && item.is_active !== false
        );
        setInventoryItems(medicineItems);
      } catch (err) {
        console.error('Failed to load inventory items:', err);
        setInventoryItems([]);
      } finally {
        setLoadingInventory(false);
      }
    };
    loadInventoryItems();
  }, [selectedLocation]);

  // Update defaults when vaccine type changes (only for vaccination mode)
  useEffect(() => {
    if (treatmentMode === 'vaccination' && vaccineType && inventoryItems.length > 0) {
      const defaults = VACCINE_DEFAULTS[vaccineType];
      // Try to find matching inventory item by name
      const matchingItem = inventoryItems.find((item: InventoryItem) => 
        item.name.toLowerCase().includes(defaults.medicine.toLowerCase()) ||
        defaults.medicine.toLowerCase().includes(item.name.toLowerCase())
      );
      if (matchingItem) {
        form.setValue('item_id', matchingItem.id);
      }
      form.setValue('dose', defaults.dose);
      form.setValue('route', defaults.route);
    }
  }, [treatmentMode, vaccineType, inventoryItems]); // Include inventoryItems in dependencies

  // Reset form when treatment mode changes
  useEffect(() => {
    if (treatmentMode === 'deworming') {
      form.setValue('vaccine_type', '' as any);
      form.setValue('route', '');
      form.setValue('dose', '');
      form.setValue('item_id', '');
      form.setValue('medicine_name', '');
    } else if (treatmentMode === 'vaccination') {
      form.setValue('vaccine_type', 'PPR');
      form.setValue('dose', VACCINE_DEFAULTS.PPR.dose);
      form.setValue('route', VACCINE_DEFAULTS.PPR.route);
      form.setValue('medicine_name', '');
    }
  }, [treatmentMode]);

  // Load pre-selected goats from URL params (from recommendations)
  useEffect(() => {
    const preSelectedTags = searchParams.get('goats');
    if (preSelectedTags) {
      const tags = preSelectedTags.split(',').filter(Boolean);
      setSelectedTags(new Set(tags));
      setIncludeMode(true); // Start in include mode when pre-selected
    }
  }, [searchParams]);

  // Load pre-selected vaccine type from URL params
  useEffect(() => {
    const preVaccineType = searchParams.get('vaccine_type') as VaccineType | null;
    if (preVaccineType && ['PPR', 'ET_TT', 'FMD_HS_BQ', 'GOAT_POX'].includes(preVaccineType)) {
      form.setValue('treatment_mode', 'vaccination');
      form.setValue('vaccine_type', preVaccineType);
      const defaults = VACCINE_DEFAULTS[preVaccineType];
      // Try to find matching inventory item by name
      if (inventoryItems.length > 0) {
        const matchingItem = inventoryItems.find((item: InventoryItem) => 
          item.name.toLowerCase().includes(defaults.medicine.toLowerCase()) ||
          defaults.medicine.toLowerCase().includes(item.name.toLowerCase())
        );
        if (matchingItem) {
          form.setValue('item_id', matchingItem.id);
        }
      }
      form.setValue('dose', defaults.dose);
      form.setValue('route', defaults.route);
    }
  }, [searchParams, form, inventoryItems]);

  useEffect(() => {
    if (businessType === 'goat' && selectedLocation?.id) {
      loadGoats();
    }
  }, [selectedLocation?.id, businessType]);

  const loadGoats = async () => {
    try {
      setLoadingGoats(true);
      const response = await apiClient.get(`/goat/master?location_id=${selectedLocation?.id}&status=active`);
      setGoats(response.data || []);
    } catch (err) {
      console.error('Failed to load goats:', err);
    } finally {
      setLoadingGoats(false);
    }
  };

  // Calculate age in months for a goat
  const calculateAgeMonths = (goat: GoatMaster): number | null => {
    // First priority: Calculate from Date of Birth (most accurate)
    if (goat.dob) {
      const birthDate = new Date(goat.dob);
      // Check if date is valid
      if (isNaN(birthDate.getTime())) {
        console.warn('Invalid dob for goat:', goat.tag_no, goat.dob);
        return null;
      }
      
      const referenceDate = new Date();
      
      // Calculate age properly accounting for whether birthday has occurred
      let years = referenceDate.getFullYear() - birthDate.getFullYear();
      let months = referenceDate.getMonth() - birthDate.getMonth();
      let days = referenceDate.getDate() - birthDate.getDate();
      
      // Adjust if birthday hasn't occurred this year
      if (days < 0) {
        months--;
        // Get days in the previous month
        const prevMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 0);
        days += prevMonth.getDate();
      }
      
      // Adjust if birthday month hasn't occurred this year
      if (months < 0) {
        years--;
        months += 12;
      }
      
      // Handle negative age (future date)
      if (years < 0 || (years === 0 && months < 0)) {
        console.warn('Negative age calculated for goat:', goat.tag_no, 'dob:', goat.dob);
        return null;
      }
      
      const ageMonths = years * 12 + months;
      return ageMonths;
    }

    // Second priority: Calculate from age_months (age at purchase) + months since purchase
    if (goat.age_months !== undefined && goat.age_months !== null && goat.purchase_date) {
      const purchaseDate = new Date(goat.purchase_date);
      // Check if date is valid
      if (isNaN(purchaseDate.getTime())) {
        console.warn('Invalid purchase_date for goat:', goat.tag_no, goat.purchase_date);
        return null;
      }
      
      const referenceDate = new Date();
      
      // Calculate months since purchase properly
      let yearsSincePurchase = referenceDate.getFullYear() - purchaseDate.getFullYear();
      let monthsSincePurchase = referenceDate.getMonth() - purchaseDate.getMonth();
      let daysSincePurchase = referenceDate.getDate() - purchaseDate.getDate();
      
      // Adjust if purchase date day hasn't occurred this month
      if (daysSincePurchase < 0) {
        monthsSincePurchase--;
        // Get days in the previous month
        const prevMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 0);
        daysSincePurchase += prevMonth.getDate();
      }
      
      // Adjust if purchase date month hasn't occurred this year
      if (monthsSincePurchase < 0) {
        yearsSincePurchase--;
        monthsSincePurchase += 12;
      }
      
      // Handle negative time since purchase (future purchase date)
      if (yearsSincePurchase < 0 || (yearsSincePurchase === 0 && monthsSincePurchase < 0)) {
        console.warn('Invalid purchase_date (future date) for goat:', goat.tag_no, goat.purchase_date);
        return null;
      }
      
      // Calculate total age in months
      const totalMonthsSincePurchase = yearsSincePurchase * 12 + monthsSincePurchase;
      const currentAgeMonths = goat.age_months + totalMonthsSincePurchase;
      
      // If result is negative, return null
      if (currentAgeMonths < 0) {
        console.warn('Negative age calculated for goat:', goat.tag_no, 'age_months:', goat.age_months, 'purchase_date:', goat.purchase_date);
        return null;
      }
      
      return currentAgeMonths;
    }

    // If age_months is set but no purchase_date, we can't calculate current age accurately
    if (goat.age_months !== undefined && goat.age_months !== null && !goat.purchase_date) {
      console.warn('Goat has age_months but no purchase_date, cannot calculate current age:', goat.tag_no);
      return null;
    }

    // No age data available
    return null;
  };

  // Filter goats by search term and age
  const filteredGoats = useMemo(() => {
    // First filter by search term
    let result = goats.filter(goat => {
      if (searchTerm && !goat.tag_no.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      return true;
    });
    
    // Then filter by age if vaccine type is selected (only for vaccination mode)
    if (treatmentMode === 'vaccination' && vaccineType) {
      const minAge = VACCINE_DEFAULTS[vaccineType].minAgeMonths;
      result = result.filter(goat => {
        // Filter by age - be lenient: show goats even if age is unknown
        const ageMonths = calculateAgeMonths(goat);
        // Only filter out if we know age and it's too young
        return ageMonths === null || ageMonths >= minAge;
      });
    }
    
    return result;
  }, [goats, searchTerm, treatmentMode, vaccineType]);

  // Get eligible goats (old enough or age unknown - be lenient)
  const eligibleGoats = useMemo(() => {
    // For deworming, all goats are eligible (no age restrictions)
    if (treatmentMode === 'deworming' || !vaccineType) return goats;
    
    // For vaccination, filter by age
    const minAge = VACCINE_DEFAULTS[vaccineType].minAgeMonths;
    return goats.filter(goat => {
      const ageMonths = calculateAgeMonths(goat);
      // Include if age is unknown (null) OR if age meets minimum requirement
      // Be very lenient - only exclude if we're certain the goat is too young
      if (ageMonths === null) {
        // Age unknown - include it (let user decide)
        return true;
      }
      // Age known - check if meets minimum
      return ageMonths >= minAge;
    });
  }, [goats, treatmentMode, vaccineType]);
  
  // Debug: Log goat data
  useEffect(() => {
    if (goats.length > 0) {
      console.log('Goats loaded:', goats.length);
      console.log('Eligible goats:', eligibleGoats.length);
      console.log('Filtered goats:', filteredGoats.length);
      if (goats.length > 0) {
        console.log('Sample goat:', {
          tag: goats[0].tag_no,
          dob: goats[0].dob,
          purchase_date: goats[0].purchase_date,
          age: calculateAgeMonths(goats[0])
        });
      }
      if (vaccineType) {
        console.log('Vaccine type:', vaccineType, 'Min age:', VACCINE_DEFAULTS[vaccineType].minAgeMonths);
      }
    }
  }, [goats, eligibleGoats, filteredGoats, vaccineType]);

  // In exclude mode, all eligible goats are selected by default
  useEffect(() => {
    if (!includeMode && (treatmentMode === 'vaccination' ? vaccineType : true)) {
      setSelectedTags(new Set(eligibleGoats.map(g => g.tag_no)));
    }
  }, [includeMode, treatmentMode, vaccineType, eligibleGoats]);

  const toggleGoat = (tag: string) => {
    const newSelected = new Set(selectedTags);
    if (newSelected.has(tag)) {
      newSelected.delete(tag);
    } else {
      newSelected.add(tag);
    }
    setSelectedTags(newSelected);
  };

  const selectAll = () => {
    setSelectedTags(new Set(filteredGoats.map(g => g.tag_no)));
  };

  const deselectAll = () => {
    setSelectedTags(new Set());
  };

  const handleSubmit = async (data: GoatVaccinationFormData) => {
    if (selectedTags.size === 0) {
      setError('Please select at least one goat');
      return;
    }

    try {
      setError('');
      setSubmitting(true);
      
      if (data.treatment_mode === 'vaccination') {
        const response = await apiClient.post('/goat/health-logs/bulk-vaccination', {
          location_id: selectedLocation?.id,
          health_date: data.health_date,
          vaccine_type: data.vaccine_type,
          item_id: data.item_id,
          dose: data.dose,
          goat_tags: Array.from(selectedTags),
          notes: data.notes || undefined,
        });

        if (response.data && response.data.length > 0) {
          navigate('/goat/health-logs');
        } else {
          setError('Failed to create vaccination records');
        }
      } else {
        // Deworming: Find inventory item by medicine name
        const matchingItem = inventoryItems.find((item: InventoryItem) => 
          item.name.toLowerCase() === data.medicine_name.toLowerCase()
        );
        
        if (!matchingItem) {
          setError(`Medicine "${data.medicine_name}" not found in inventory. Please add it to inventory first.`);
          setSubmitting(false);
          return;
        }
        
        const response = await apiClient.post('/goat/health-logs/bulk-deworming', {
          location_id: selectedLocation?.id,
          health_date: data.health_date,
          item_id: matchingItem.id,
          dose: data.dose,
          goat_tags: Array.from(selectedTags),
          notes: data.notes || undefined,
        });

        if (response.data && response.data.length > 0) {
          navigate('/goat/health-logs');
        } else {
          setError('Failed to create deworming records');
        }
      }
    } catch (err: any) {
      const errorMsg = data.treatment_mode === 'vaccination' 
        ? 'Failed to create vaccination records'
        : 'Failed to create deworming records';
      setError(getErrorMessage(err, errorMsg));
    } finally {
      setSubmitting(false);
    }
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This form is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  const selectedCount = selectedTags.size;
  const totalEligible = eligibleGoats.length;
  const tooYoungCount = goats.length - totalEligible;

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/goat/health-logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {treatmentMode === 'vaccination' ? '💉 Add Vaccination' : '💊 Add Deworming'}
          </h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Form */}
      <div className="card p-6">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Treatment Type *</label>
              <select {...form.register('treatment_mode')} className="form-input">
                <option value="vaccination">💉 Vaccination</option>
                <option value="deworming">💊 Deworming</option>
              </select>
              {form.formState.errors.treatment_mode && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.treatment_mode.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Health Date *</label>
              <input
                type="date"
                {...form.register('health_date')}
                className="form-input"
              />
              {form.formState.errors.health_date && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.health_date.message}</p>
              )}
            </div>

            {treatmentMode === 'vaccination' && (
              <div>
                <label className="form-label">Vaccine Type *</label>
                <select {...form.register('vaccine_type')} className="form-input">
                  <option value="PPR">PPR</option>
                  <option value="ET_TT">ET + TT</option>
                  <option value="FMD_HS_BQ">FMD + HS + BQ (Triovac)</option>
                  <option value="GOAT_POX">Goat Pox</option>
                </select>
                {form.formState.errors.vaccine_type && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.vaccine_type.message}</p>
                )}
              </div>
            )}

            <div>
              <label className="form-label">Medicine *</label>
              {treatmentMode === 'vaccination' ? (
                // For vaccination: show inventory items
                loadingInventory ? (
                  <div className="form-input bg-gray-50 flex items-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-gray-500">Loading inventory items...</span>
                  </div>
                ) : (
                  <select 
                    {...form.register('item_id')} 
                    className="form-input"
                    value={form.watch('item_id')}
                    onChange={(e) => form.setValue('item_id', e.target.value)}
                  >
                    <option value="">Select Medicine</option>
                    {inventoryItems.map((item: InventoryItem) => (
                      <option key={item.id} value={item.id}>
                        {item.name} {item.unit ? `(${item.unit})` : ''}
                      </option>
                    ))}
                  </select>
                )
              ) : (
                // For deworming: show all goat medicines from master data
                loadingMedicines ? (
                  <div className="form-input bg-gray-50 flex items-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-gray-500">Loading medicines...</span>
                  </div>
                ) : (
                  <select 
                    {...form.register('medicine_name')} 
                    className="form-input"
                    value={form.watch('medicine_name')}
                    onChange={(e) => form.setValue('medicine_name', e.target.value)}
                  >
                    <option value="">Select Medicine</option>
                    {medicines.map((medicine: any) => {
                      // Check if medicine is available in inventory
                      const inInventory = inventoryItems.some((item: InventoryItem) => 
                        item.name.toLowerCase() === medicine.name.toLowerCase()
                      );
                      return (
                        <option key={medicine.id} value={medicine.name}>
                          {medicine.name} {medicine.unit ? `(${medicine.unit})` : ''}
                          {inInventory ? ' ✓' : ' (Not in inventory)'}
                        </option>
                      );
                    })}
                  </select>
                )
              )}
              {treatmentMode === 'vaccination' && form.formState.errors.item_id && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.item_id.message}</p>
              )}
              {treatmentMode === 'deworming' && form.formState.errors.medicine_name && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.medicine_name.message}</p>
              )}
              {treatmentMode === 'vaccination' && inventoryItems.length === 0 && !loadingInventory && (
                <p className="mt-1 text-sm text-orange-600">
                  No medicine items found in inventory. Please add inventory purchases first.
                </p>
              )}
              {treatmentMode === 'deworming' && medicines.length === 0 && !loadingMedicines && (
                <p className="mt-1 text-sm text-orange-600">
                  No goat medicines found. Please add medicines to master data first.
                </p>
              )}
            </div>

            <div>
              <label className="form-label">Dose *</label>
              <input
                type="text"
                {...form.register('dose')}
                className="form-input"
                placeholder="e.g., 1 ml, 0.5 ml"
              />
              {form.formState.errors.dose && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.dose.message}</p>
              )}
            </div>

            {treatmentMode === 'vaccination' && (
              <div>
                <label className="form-label">Route *</label>
                <select 
                  {...form.register('route')} 
                  className="form-input"
                  value={form.watch('route')}
                  onChange={(e) => form.setValue('route', e.target.value)}
                >
                  <option value="Subcutaneous (SC)">Subcutaneous (SC)</option>
                  <option value="Intramuscular (IM)">Intramuscular (IM)</option>
                </select>
                {form.formState.errors.route && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.route.message}</p>
                )}
              </div>
            )}

            <div className="sm:col-span-2">
              <label className="form-label">Notes</label>
              <textarea
                {...form.register('notes')}
                className="form-input"
                rows={3}
                placeholder="Additional notes about the vaccination"
              />
            </div>
          </div>

          {/* Goat Selection Section */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Select Goats</h3>
                <p className="text-sm text-gray-600">
                  {selectedCount} selected of {totalEligible} eligible goats
                  {treatmentMode === 'vaccination' && tooYoungCount > 0 && (
                    <span className="text-orange-600 ml-2">
                      ({tooYoungCount} too young for this vaccine)
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setIncludeMode(!includeMode)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    includeMode
                      ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                      : 'bg-green-100 text-green-800 hover:bg-green-200'
                  }`}
                >
                  {includeMode ? 'Include Mode' : 'Exclude Mode'}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                placeholder="Search by tag number..."
              />
            </div>

            <div className="flex space-x-2 mb-4">
              <button
                type="button"
                onClick={selectAll}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={deselectAll}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Deselect All
              </button>
            </div>

            {loadingGoats ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : goats.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No active goats found for this location
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                {filteredGoats.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    {searchTerm ? (
                      'No goats found matching your search'
                    ) : (
                      <>
                        <p>No eligible goats found. Total goats: {goats.length}, Eligible: {eligibleGoats.length}</p>
                        {treatmentMode === 'vaccination' && vaccineType && (
                          <div className="mt-2 text-xs text-gray-400">
                            Minimum age required: {VACCINE_DEFAULTS[vaccineType].minAgeMonths} months
                          </div>
                        )}
                        {goats.length > 0 && (
                          <div className="mt-4 text-sm">
                            <p className="font-medium mb-2">To fix this, add Date of Birth or Purchase Date to your goats:</p>
                            <Link
                              to="/goat/master"
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              Go to Goat Master →
                            </Link>
                            <p className="mt-2 text-xs text-gray-500">
                              Goats without age data are excluded. Add DOB or Purchase Date to include them.
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredGoats.map((goat) => {
                      const isSelected = selectedTags.has(goat.tag_no);
                      const ageMonths = calculateAgeMonths(goat);
                      const hasAgeData = !!(goat.dob || goat.purchase_date);
                      return (
                        <label
                          key={goat.id}
                          className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                            isSelected ? 'bg-blue-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleGoat(goat.tag_no)}
                            className="mr-3 h-4 w-4 text-blue-600"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {goat.tag_no} ({goat.category})
                            </div>
                            {ageMonths !== null ? (
                              <div className="text-sm text-gray-500">
                                Age: {ageMonths} months
                                {treatmentMode === 'vaccination' && vaccineType && ageMonths < VACCINE_DEFAULTS[vaccineType].minAgeMonths && (
                                  <span className="ml-2 text-orange-600 text-xs">(Too young)</span>
                                )}
                              </div>
                            ) : hasAgeData ? (
                              <div className="text-sm text-gray-500">
                                Age: Unable to calculate (check dates)
                              </div>
                            ) : (
                              <div className="text-sm text-orange-600">
                                No age data - Add DOB or Purchase Date in{' '}
                                <Link 
                                  to={`/goat/master/${goat.id}/edit`}
                                  className="underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Goat Master
                                </Link>
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={submitting || selectedCount === 0}
              className="flex-1 btn-touch bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {submitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Creating Records...</span>
                </>
              ) : (
                `Record ${treatmentMode === 'vaccination' ? 'Vaccination' : 'Deworming'} (${selectedCount} goats)`
              )}
            </button>
            <Link
              to="/goat/health-logs"
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoatVaccinationForm;
